import { useState } from 'react';
import { useProgressTracker } from '../hooks/useProgressTracker';

const LETTERS = ['A', 'B', 'C', 'D'];

export default function QuizPlayer({ questions, topicKey, subtopic, onComplete }) {
  const [current, setCurrent]   = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore]       = useState(0);
  const [done, setDone]         = useState(false);
  const { recordQuizScore } = useProgressTracker();

  const q = questions[current];
  const pct = (current / questions.length) * 100;

  const handleSelect = (i) => { if (!answered) setSelected(i); };

  const handleCheck = () => {
    if (selected === null) return;
    setAnswered(true);
    if (selected === q.correct) setScore((s) => s + 1);
  };

  const handleNext = () => {
    if (current + 1 >= questions.length) {
      // Record quiz score when quiz completes
      const finalScore = score + (selected === q.correct ? 1 : 0);
      const pctScore = Math.round((finalScore / questions.length) * 100);
      if (topicKey && subtopic) {
        recordQuizScore(topicKey, subtopic, pctScore);
      }
      setDone(true);
      return;
    }
    setCurrent((c) => c + 1);
    setSelected(null);
    setAnswered(false);
  };

  const handleRetake = () => {
    setCurrent(0); setSelected(null); setAnswered(false); setScore(0); setDone(false);
  };

  if (done) {
    const pctScore = Math.round((score / questions.length) * 100);
    const color = pctScore >= 80 ? 'var(--green)' : pctScore >= 70 ? 'var(--amber)' : 'var(--red)';
    const emoji = pctScore >= 80 ? '🎯' : pctScore >= 70 ? '📈' : '📚';
    const msg   = pctScore >= 80 ? "Outstanding! You've mastered this lesson." : pctScore >= 70 ? 'Good work — review any missed questions.' : 'Review the lesson content and try again.';
    return (
      <div className="quiz-result-card">
        <div style={{ fontSize: 44, marginBottom: 12 }}>{emoji}</div>
        <div className="quiz-category">{"// LESSON QUIZ COMPLETE"}</div>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Quiz Results</div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 52, fontWeight: 500, color, lineHeight: 1, margin: '12px 0' }}>{pctScore}%</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 24 }}>{score} of {questions.length} correct — {msg}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="qa-btn secondary" onClick={handleRetake}>Retake Quiz</button>
          {onComplete && <button className="qa-btn primary" onClick={onComplete}>Continue →</button>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="lq-progress-row">
        <div className="lq-counter">Q{current + 1} / {questions.length}</div>
        <div className="lq-bar"><div className="lq-bar-fill" style={{ width: `${pct}%` }} /></div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)' }}>{score}/{current} correct</div>
      </div>

      <div className="quiz-category">{q.category}</div>
      <div className="quiz-question">{q.question}</div>

      <div className="quiz-options">
        {q.options.map((opt, i) => {
          let cls = '';
          if (answered) {
            if (i === q.correct) cls = 'correct';
            else if (i === selected) cls = 'incorrect';
          } else if (i === selected) {
            cls = 'selected';
          }
          return (
            <button key={i} className={`quiz-option ${cls}`} onClick={() => handleSelect(i)} disabled={answered}>
              <div className="opt-letter">{LETTERS[i]}</div>
              <span>{opt}</span>
            </button>
          );
        })}
      </div>

      {answered && (
        <div className="quiz-explanation">
          <strong>✓ Explanation:</strong>{' '}
          <span dangerouslySetInnerHTML={{ __html: q.explanation }} />
        </div>
      )}

      <div className="quiz-actions">
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }} />
        <div style={{ display: 'flex', gap: 8 }}>
          {!answered && (
            <button className="qa-btn primary" onClick={handleCheck} disabled={selected === null}>
              CHECK ANSWER
            </button>
          )}
          {answered && (
            <button className="qa-btn secondary" onClick={handleNext}>
              {current + 1 >= questions.length ? 'SEE RESULTS →' : 'NEXT →'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
